import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactModal from 'react-modal';
import styles from './Dashboard.module.css';
import { useActivePage } from '../../contexts/ActivePageContext';
import { useAuth } from '../../contexts/AuthContext';

import arrowLeft from '../../assets/icons/png/arrow-left-bold.png';
import logoFull from '../../assets/loggableLogos/LoggableIconHorizontal.png';
import logoIcon from '../../assets/loggableLogos/LoggableIcon.png';
import logoutIcon from '../../assets/icons/svg/logout.svg';
import accountIcon from '../../assets/icons/svg/account-circle-outline.svg';
import dashboardIcon from '../../assets/icons/svg/view-dashboard-outline.svg';
import logbooksIcon from '../../assets/icons/svg/format-list-checkbox.svg';
import exportIcon from '../../assets/icons/svg/file-export-outline.svg';
import settingsIcon from '../../assets/icons/svg/cog-outline.svg';
import menuIcon from '../../assets/icons/svg/menu.svg'; 
import { ReactComponent as Close } from '../../assets/icons/svg/close-box-outline.svg';

export default function Sidebar({ onLogout }) {
  const [isOpen, setIsOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const { activePage, setActivePage } = useActivePage();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const { getValidToken } = useAuth();

  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const navItems = [
    { name: 'Dashboard', key: 'overview', icon: dashboardIcon, id: 'dashboard' },
    { name: 'Logbooks', key: 'logbooks', icon: logbooksIcon, id: 'logbooks' },
    { name: 'Export', key: 'logbooks', icon: exportIcon, id: 'export' },
    { name: 'Settings', key: 'settings', icon: settingsIcon, id: 'settings' },
  ];

  useEffect(() => {
    if (isAccountModalOpen) {
      checkActiveCustomerSubscription();
    }
  }, [isAccountModalOpen]);

  const checkActiveCustomerSubscription = async () => {
  const API_URL =
    "https://p4og1sw6yg.execute-api.us-east-1.amazonaws.com/prod/checkCustomer";

  try {
    // Retrieve Cognito user token
    const validToken = await getValidToken();

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${validToken}`,
        "Content-Type": "application/json",
      },
    });

    // Handle 404: dump everything
    if (response.status === 404) {
      console.warn("Customer not found. Clearing subscription data.");
      onLogout();
      return null;
    }

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log("Subscription API response:", data);

    // Flatten trialLeft to a number
    const trialRemaining = data.trialLeft?.[0]?.Trial ?? 0;

    // Handle subscription metadata and calculate days left
    let subscriptionMetadata = data.subscriptionMetadata;
    let daysLeft = 0;

    if (subscriptionMetadata?.current_period_end) {
      const currentDate = new Date();
      const periodEndDate = new Date(subscriptionMetadata.current_period_end);
      daysLeft = Math.floor((periodEndDate - currentDate) / (1000 * 60 * 60 * 24));

      // Remove metadata if subscription expired
      if (daysLeft <= 0) {
        subscriptionMetadata = null;
        daysLeft = 0;
      }
    }

    // Store in localStorage
    localStorage.setItem("isActiveSubscriber", String(data.isActiveSubscriber));
    localStorage.setItem("subscriberMetadata", JSON.stringify(subscriptionMetadata));
    localStorage.setItem("trialLeft", String(trialRemaining));
    localStorage.setItem("daysLeft", String(daysLeft));

    // Update React state
    setSubscriptionData({
      ...data,
      trialLeft: trialRemaining,
      subscriptionMetadata,
      daysLeft,
    });

    return data;

  } catch (error) {
    console.error("Error in checkActiveCustomerSubscription:", error);
    return null;
  }
};



  const cancelCustomerSubscription = async () => {
    const API_URL = 'https://p4og1sw6yg.execute-api.us-east-1.amazonaws.com/prod/cancellation';

    try {
      const validToken = await getValidToken();

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${validToken}`,
          'Content-Type': 'application/json'
        },
      });

      const data = await response.json();
      console.log('Subscription cancellation/resubscription result:', data);

      // Save updated subscription metadata to localStorage
      localStorage.setItem('subscriberMetadata', JSON.stringify(data.subscriptionMetadata));

      // Update React state
      setSubscriptionData((prev) => ({
        ...prev,
        subscriptionMetadata: data.subscriptionMetadata,
        isActiveSubscriber: !data.subscriptionMetadata.cancel_at_period_end,
      }));

      return data;

    } catch (error) {
      console.error('Error performing subscription action:', error);
    }
  };

  const daysLeft = (() => {
    if (!subscriptionData?.subscriptionMetadata?.current_period_end) return "--";

    const currentDate = new Date();
    const periodEndDate = new Date(subscriptionData.subscriptionMetadata.current_period_end);
    const timeDifference = periodEndDate - currentDate;

    return Math.max(0, Math.floor(timeDifference / (1000 * 60 * 60 * 24)));
  })();



  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`${styles.sidebar} ${isOpen ? styles.open : styles.collapsed}`}>
        <div className={styles.sidebarHeader}>
          <img
            src={logoFull}
            alt="Full Logo"
            className={`${styles.sidebarLogo} ${isOpen ? styles.logoVisible : styles.logoHidden}`}
          />
          <img
            src={logoIcon}
            alt="Icon Logo"
            className={`${styles.sidebarLogo} ${!isOpen ? styles.logoVisible : styles.logoHidden}`}
          />
          <img
            src={arrowLeft}
            alt="Toggle"
            className={styles.sidebarToggle}
            onClick={toggleSidebar}
          />
        </div>

        <ul className={styles.sidebarNav}>
          {navItems.map((item) => (
            <li
              key={item.id}
              onClick={() => setActivePage(item.id)}
              className={`${styles.sidebarLink} ${activePage === item.id ? styles.active : ''}`}
            >
              <img src={item.icon} alt="" className={styles.navIcon} />
              {isOpen && <span>{item.name}</span>}
            </li>
          ))}
        </ul>

        <div className={styles.sidebarBottom}>
          {/* Account button opens modal */}
          <button
            type="button"
            className={styles.sidebarLink}
            onClick={() => setIsAccountModalOpen(true)}
          >
            <img src={accountIcon} alt="Account" className={styles.navIcon} />
            {isOpen && user && <span>{user.firstName} {user.lastName}</span>}
          </button>

          <button type="button" className={styles.sidebarLink} onClick={onLogout}>
            <img src={logoutIcon} alt="Logout" className={styles.navIcon} />
            {isOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className={styles.mobileHeader}>
        <img src={logoIcon} alt="Small Logo" className={styles.sidebarLogo} />
        <button className={styles.hamburgerButton} onClick={toggleMobileMenu}>
          <img src={menuIcon} alt="Menu" className={styles.navIcon} />
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.open : ''}`}>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={styles.sidebarLink}
              onClick={() => {
                setActivePage(item.id);
                setMobileMenuOpen(false);
              }}
            >
              <img src={item.icon} alt="" className={styles.navIcon} />
              <span>{item.name}</span>
            </button>
          ))}
          <hr />
          <button
            type="button"
            className={styles.sidebarLink}
            onClick={() => setIsAccountModalOpen(true)}
          >
            <img src={accountIcon} alt="Account" className={styles.navIcon} />
            {user && <span>{user.firstName} {user.lastName}</span>}
          </button>
          <button type="button" className={styles.sidebarLink} onClick={onLogout}>
            <img src={logoutIcon} alt="Logout" className={styles.navIcon} />
            <span>Logout</span>
          </button>
        </div>
      )}

      {/* Account Info Modal */}
      <ReactModal
        isOpen={isAccountModalOpen}
        onRequestClose={() => setIsAccountModalOpen(false)}
        contentLabel="Account Info Modal"
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
        ariaHideApp={false}
      >
        <div className={styles.modalHeaderRow}>
          <h2 className={styles.modalHeader}>Account Information</h2>
          <button onClick={() => setIsAccountModalOpen(false)} className={styles.closeButton} aria-label="Close" > <Close width={30} height={30} fill="red" /> </button>
        </div>

        <div className={styles.modalDetails}>
          {user ? (
            <>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Name</span>
                <span>{user.firstName} {user.lastName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Email</span>
                <span>{user.email}</span>
              </div>

              {subscriptionData ? (
                <>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Status</span>
                    <span
                      className={
                        subscriptionData.isActiveSubscriber
                          ? styles.activeBadge
                          : styles.inactiveBadge
                      }
                    >
                      {subscriptionData.isActiveSubscriber ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {/* Show Trial Logs Left only when not subscribed */}
                  {!subscriptionData.subscriptionMetadata && (
                    <>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Trial Logs Left</span>
                        <span>{subscriptionData.trialLeft ?? 0}</span>
                      </div>
                      <button
                        style={{ backgroundColor: "#107C41", color: "white", borderRadius: 8, padding: "10px 20px", fontSize: 16, cursor: "pointer", border: "none" }}
                        onClick={() => { setIsAccountModalOpen(false); setActivePage("payment"); }}
                      >
                        Subscribe Now
                      </button>
                    </>
                  )}

                  {subscriptionData.subscriptionMetadata && (
                    <>
                      <div className={styles.subscriptionBox}>
                        <div className={styles.daysLeft}>{daysLeft}</div>
                        <div className={styles.details}>Subscription days left</div>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Period Start</span>
                        <span>
                          {new Date(
                            subscriptionData.subscriptionMetadata.current_period_start
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Period End</span>
                        <span>
                          {new Date(
                            subscriptionData.subscriptionMetadata.current_period_end
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Cancel at Period End</span>
                        <span>
                          {subscriptionData.subscriptionMetadata.cancel_at_period_end
                            ? "Yes"
                            : "No"}
                        </span>
                      </div>

                      <div className={styles.modalActions}>
                        <button
                          className={
                            subscriptionData.subscriptionMetadata.cancel_at_period_end
                              ? styles.primaryButton
                              : styles.dangerButton
                          }
                          onClick={async () => {
                            const action = subscriptionData.subscriptionMetadata
                              .cancel_at_period_end
                              ? "resubscribe"
                              : "cancel";

                            if (
                              window.confirm(
                                `Are you sure you want to ${action} your subscription?`
                              )
                            ) {
                              try {
                                await cancelCustomerSubscription();
                                alert(
                                  subscriptionData.subscriptionMetadata.cancel_at_period_end
                                    ? "Resubscribed successfully!"
                                    : "Subscription cancelled."
                                );
                              } catch (error) {
                                console.error(
                                  "Error performing subscription action:",
                                  error
                                );
                                alert("Something went wrong. Please try again.");
                              }
                            }
                          }}
                        >
                          {subscriptionData.subscriptionMetadata.cancel_at_period_end
                            ? "Resubscribe Now!"
                            : "Cancel Subscription"}
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p>Loading subscription info...</p>
              )}
            </>
          ) : (
            <p>No account info available.</p>
          )}
        </div>
      </ReactModal>

    </>
  );
}
